import * as React from 'react'
import { useParams } from 'react-router-dom'

import { SurveyShowPagePoliciesDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import asyncAction from 'modules/core/lib/asyncAction'
import { apiClient } from 'modules/core/lib/mobx-rest'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'

import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useEngagementContext } from 'modules/engagement/hooks/useEngagementContext'
import SurveyShowWrapper from 'modules/surveys/components/Show/SurveyShowWrapper'
import { useQuestionGroups } from 'modules/surveys/hooks/useQuestionGroups'
import { useSurvey } from 'modules/surveys/hooks/useSurvey'
import { useSurveyFormV1 } from 'modules/surveys/hooks/useSurveyFormV1'
import { canEditSurvey } from 'modules/surveys/lib/canEditSurvey'

import { SurveyShowContext } from './hooks'

const SurveyShowPage = () => {
  const { id } = useParams()
  const { loading, form } = useSurveyFormV1(String(id))
  const { loading: loadingSurvey, survey } = useSurvey(id)
  const { loading: loadingQuestionGroups, questionGroups } = useQuestionGroups(String(id))

  const { data: policiesData } = useQuery(SurveyShowPagePoliciesDocument, {
    variables: { surveyId: id ?? '' },
    skip: !id,
  })

  const { canManageSurveys } = useEngagementContext()
  const { has_surveys_using_forms: smartSurveysEnabled, loadingFeatures } = useFeatures([
    'surveys_using_forms',
  ])
  const [canbeStarted, setCanBeStarted] = React.useState(false)

  const fetchCanBeStarted = async () => {
    const api = apiClient()
    const result = await api.get(`/surveys/v2/surveys/${id}/status`)
    setCanBeStarted(result.can_be_started)
  }

  const updateCanBeStarted = async () =>
    await asyncAction(fetchCanBeStarted, {
      errorFlash: false,
      successFlash: false,
    })

  if (loading || loadingSurvey || loadingQuestionGroups) return <Loading />

  if (!loadingSurvey && !survey) return <NotFound />

  const canEdit = survey ? canEditSurvey(survey, canManageSurveys) : false
  const canEditForm = policiesData?.surveys?.survey?._policies?.edit?.can ?? false
  const isClassicCustomSurvey =
    !survey?.attributes.source || survey.attributes.source === 'custom_survey'

  const author = policiesData?.surveys?.survey?.author
  const authorName = author
    ? getFullDisplayName({ firstName: author.firstName, lastName: author.lastName })
    : null

  const surveyShowContextValues = {
    form: form,
    survey: survey,
    hasSurveyV2: true,
    isAnonymous: !!survey && survey.isAnonymous,
    canbeStarted: canbeStarted,
    updateCanBeStarted: updateCanBeStarted,
    canEditSurvey: canEdit,
    canEditSurveyForm: canEditForm,
    loading: loadingSurvey,
    isClassicSurveyDisabled: (loadingFeatures || smartSurveysEnabled) && isClassicCustomSurvey,
    authorName,
  }

  return (
    <SurveyShowContext.Provider value={surveyShowContextValues}>
      <SurveyShowWrapper surveyId={String(id)} questionGroups={questionGroups} />
    </SurveyShowContext.Provider>
  )
}

export default SurveyShowPage
